import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Loader2, Tag, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { DateTimeInput } from '../components/ui/date-time-input';
import { refuseIfDateInputIncomplete } from '../lib/dateInputs';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import apiService from '../services/api.service';
import { toast } from 'sonner';

interface PromoCode {
  id: string;
  code: string;
  scope: 'license' | 'registration' | 'both';
  type: 'percentage' | 'fixed_amount';
  value: number;
  max_uses: number | null;
  max_uses_per_org: number | null;
  used_count: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  organization_id: string | null;
  min_tier: string | null;
  created_at: string;
}

interface PromoForm {
  code: string;
  scope: 'license' | 'registration' | 'both';
  type: 'percentage' | 'fixed_amount';
  value: number;
  max_uses: string;
  max_uses_per_org: string;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  organization_id: string;
  min_tier: string;
}

const emptyForm: PromoForm = {
  code: '',
  scope: 'both',
  type: 'percentage',
  value: 10,
  max_uses: '',
  max_uses_per_org: '',
  valid_from: '',
  valid_until: '',
  is_active: true,
  organization_id: '',
  min_tier: '',
};

const AdminPromoCodes: React.FC = () => {
  const { t } = useTranslation();
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<PromoCode | null>(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await apiService.getAdminPromoCodes();
      setCodes(res.promo_codes || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingCode(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (code: PromoCode) => {
    setEditingCode(code);
    setForm({
      code: code.code,
      scope: code.scope,
      type: code.type,
      value: code.value,
      max_uses: code.max_uses?.toString() || '',
      max_uses_per_org: code.max_uses_per_org?.toString() || '',
      valid_from: code.valid_from?.split('T')[0] || '',
      valid_until: code.valid_until?.split('T')[0] || '',
      is_active: code.is_active,
      organization_id: code.organization_id || '',
      min_tier: code.min_tier || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (refuseIfDateInputIncomplete(t)) return;
    const payload = {
      code: form.code.toUpperCase(),
      scope: form.scope,
      type: form.type,
      value: Number(form.value),
      max_uses: form.max_uses ? Number(form.max_uses) : null,
      max_uses_per_org: form.max_uses_per_org ? Number(form.max_uses_per_org) : null,
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
      is_active: form.is_active,
      organization_id: form.organization_id || null,
      min_tier: form.min_tier || null,
    };

    try {
      if (editingCode) {
        await apiService.updatePromoCode(editingCode.id, payload);
      } else {
        await apiService.createPromoCode(payload);
      }
      setDialogOpen(false);
      await loadData();
      toast.success(editingCode ? t('admin.promo.updated') : t('admin.promo.created'));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur sauvegarde');
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await apiService.deletePromoCode(id);
      await loadData();
      toast.success(t('admin.promo.deactivated'));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const scopeLabel = (scope: string) => {
    switch (scope) {
      case 'license': return t('admin.promo.scopeLicense');
      case 'registration': return t('admin.promo.scopeRegistration');
      case 'both': return t('admin.promo.scopeBoth');
      default: return scope;
    }
  };

  const formatValue = (type: string, value: number) => {
    return type === 'percentage' ? `${value}%` : `${(value / 100).toFixed(2)}€`;
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl md:text-3xl font-bold">{t('admin.promo.title')}</h1>
        <Button size="sm" onClick={openCreate} className="w-full sm:w-auto">
          <Plus size={16} className="mr-1" />
          {t('admin.promo.create')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={32} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.promo.code')}</TableHead>
                  <TableHead>{t('admin.promo.scope')}</TableHead>
                  <TableHead>{t('admin.promo.discount')}</TableHead>
                  <TableHead>{t('admin.promo.usage')}</TableHead>
                  <TableHead>{t('admin.promo.validity')}</TableHead>
                  <TableHead>{t('admin.promo.status')}</TableHead>
                  <TableHead>{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {codes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      {t('admin.promo.noCodes')}
                    </TableCell>
                  </TableRow>
                ) : (
                  codes.map((code) => (
                    <TableRow key={code.id} className={!code.is_active ? 'opacity-50' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Tag size={14} className="text-muted-foreground" />
                          <span className="font-mono font-bold">{code.code}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(code.code);
                              toast.success('Code copied');
                            }}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{scopeLabel(code.scope)}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        -{formatValue(code.type, code.value)}
                      </TableCell>
                      <TableCell>
                        {code.used_count}{code.max_uses ? `/${code.max_uses}` : ''}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {code.valid_from || code.valid_until ? (
                          <>
                            {code.valid_from && new Date(code.valid_from).toLocaleDateString()}
                            {' → '}
                            {code.valid_until ? new Date(code.valid_until).toLocaleDateString() : '∞'}
                          </>
                        ) : (
                          t('admin.promo.noLimit')
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={code.is_active ? 'bg-success/15 text-success' : 'bg-white/[0.06] text-muted-foreground'}>
                          {code.is_active ? t('admin.promo.active') : t('admin.promo.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(code)}>
                            <Edit size={16} />
                          </Button>
                          {code.is_active && (
                            <Button variant="ghost" size="sm" onClick={() => handleDeactivate(code.id)}>
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCode ? t('admin.promo.editTitle') : t('admin.promo.createTitle')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('admin.promo.code')}</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="PROMO2026"
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('admin.promo.scope')}</Label>
                <Select value={form.scope} onValueChange={(v: any) => setForm({ ...form, scope: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="license">{t('admin.promo.scopeLicense')}</SelectItem>
                    <SelectItem value="registration">{t('admin.promo.scopeRegistration')}</SelectItem>
                    <SelectItem value="both">{t('admin.promo.scopeBoth')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('admin.promo.type')}</Label>
                <Select value={form.type} onValueChange={(v: any) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{t('admin.promo.typePercentage')}</SelectItem>
                    <SelectItem value="fixed_amount">{t('admin.promo.typeFixed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t('admin.promo.value')} {form.type === 'percentage' ? '(%)' : '(cents)'}</Label>
              <NumberInput
                value={form.value}
                onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('admin.promo.maxUses')}</Label>
                <NumberInput
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  placeholder={t('admin.promo.unlimited')}
                />
              </div>
              <div>
                <Label>{t('admin.promo.maxUsesPerOrg')}</Label>
                <NumberInput
                  value={form.max_uses_per_org}
                  onChange={(e) => setForm({ ...form, max_uses_per_org: e.target.value })}
                  placeholder={t('admin.promo.unlimited')}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('admin.promo.validFrom')}</Label>
                <DateTimeInput
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                />
              </div>
              <div>
                <Label>{t('admin.promo.validUntil')}</Label>
                <DateTimeInput
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>{t('admin.promo.orgSpecific')}</Label>
              <Input
                value={form.organization_id}
                onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                placeholder={t('admin.promo.orgSpecificPlaceholder')}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label>{t('admin.promo.isActive')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={!form.code || !form.value}>
              {editingCode ? t('common.save') : t('admin.promo.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPromoCodes;
